import {
  type CreateCustomerFuelSurchargeRequest,
  type CustomerFuelSurcharge,
  type UpdateCustomerFuelSurchargeRequest,
} from '@my/tms-api';
import { cn } from '@my/ui';
import { Check, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FUEL_TERRITORIES, type FuelSurchargeRow } from './ratesChargesMock';
import { Toggle } from './Toggle';

export interface FuelSurchargeFormProps {
  organisationId: string;
  customerPartyId: string;
  /** Existing rows — used to surface the current ("prior") rate for the name. */
  existing: FuelSurchargeRow[];
  /** When set, the form edits this surcharge in place instead of adding a new one. */
  editing?: CustomerFuelSurcharge | null;
  onCancel: () => void;
  onSave: (request: CreateCustomerFuelSurchargeRequest) => void;
  /** Required when `editing` is set — emits the update request. */
  onUpdate?: (request: UpdateCustomerFuelSurchargeRequest) => void;
}

/**
 * "Add fuel surcharge" — a named %, scoped to one country/region, valid for a
 * date window. When the name already has a Current rate, that's surfaced as the
 * "prior" rate + a banner: a new rate runs alongside it until the prior is
 * expired (rate versioning). Emits a create request.
 */
export function FuelSurchargeForm({
  organisationId,
  customerPartyId,
  existing,
  editing,
  onCancel,
  onSave,
  onUpdate,
}: FuelSurchargeFormProps) {
  const isEdit = !!editing;
  const [name, setName] = useState(editing?.name ?? '');
  const [rate, setRate] = useState(editing ? String(editing.ratePercent) : '');
  // Scoped to a territory by default; toggle off for a flat % across all deliveries.
  const [scoped, setScoped] = useState(editing ? editing.scopeKind !== 'All' : true);
  const [territoryCode, setTerritoryCode] = useState<string>(() => {
    if (editing && editing.scopeKind !== 'All') {
      const match = FUEL_TERRITORIES.find(
        (t) => t.kind === editing.scopeKind && t.code === editing.scopeCode,
      );
      return match?.code ?? editing.scopeCode ?? FUEL_TERRITORIES[0]?.code ?? '';
    }
    return FUEL_TERRITORIES[0]?.code ?? '';
  });
  const [from, setFrom] = useState(editing?.effectiveFrom ?? '');
  const [to, setTo] = useState(editing?.effectiveTo ?? '');
  const [noExpiry, setNoExpiry] = useState(editing ? !editing.effectiveTo : false);

  // The current rate for this name (if any) is the "prior" once a NEW one is
  // added — surfaced for context + the alongside-running warning. Suppressed in
  // edit mode (you're changing a row, not running a new one alongside).
  const prior = useMemo(
    () =>
      isEdit
        ? null
        : (existing.find(
            (r) =>
              r.status === 'Current' && r.name.trim().toLowerCase() === name.trim().toLowerCase(),
          ) ?? null),
    [isEdit, existing, name],
  );

  const canSave =
    name.trim() !== '' && rate.trim() !== '' && !!from && (!scoped || territoryCode !== '');

  const handleSave = () => {
    if (!canSave) return;
    const base = {
      name: name.trim(),
      ratePercent: Number(rate) || 0,
      effectiveFrom: from,
      effectiveTo: noExpiry ? null : to || null,
    };
    // Resolve the scope. A known territory chip wins; otherwise (editing a
    // legacy/other code) preserve the entity's scope kind + the raw code.
    const scope = !scoped
      ? { scopeKind: 'All' as const, scopeCode: null }
      : (() => {
          const sel = FUEL_TERRITORIES.find((t) => t.code === territoryCode);
          return {
            scopeKind: sel?.kind ?? editing?.scopeKind ?? ('Country' as const),
            scopeCode: sel?.code ?? territoryCode,
          };
        })();

    if (isEdit && editing && onUpdate) {
      onUpdate({ id: editing.id, ...base, ...scope });
      return;
    }
    onSave({ organisationId, customerPartyId, ...base, ...scope });
  };

  return (
    <div className="rounded-md border border-blue-200 bg-white">
      <div className="border-b border-blue-100 bg-blue-50/70 px-3 py-2 text-sm font-semibold text-blue-700">
        {isEdit ? 'Edit fuel surcharge' : 'Add fuel surcharge'}
      </div>

      <div className="space-y-4 p-3">
        {/* Name */}
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Active Fuel Surcharge A"
            className="w-full rounded-sm border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </Field>

        {/* Rate */}
        <Field label="Rate">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center">
              <input
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                type="number"
                step="0.1"
                min="0"
                placeholder="0.0"
                className="w-20 rounded-l-sm border border-slate-300 bg-white px-2 py-1.5 text-sm tabular-nums text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <span className="rounded-r-sm border border-l-0 border-slate-300 bg-slate-50 px-2 py-1.5 text-sm text-slate-500">
                %
              </span>
            </div>
            <span className="text-xs text-slate-400">of transport charge</span>
            {prior && (
              <span className="rounded bg-slate-100 px-2 py-1 text-[11px] text-slate-500">
                Prior rate: <span className="font-semibold text-slate-700">{prior.rate}</span> — active
                from {prior.fromLabel}
              </span>
            )}
          </div>
        </Field>

        {/* Territory — flat (all deliveries) or scoped to a country/region. When
            scoped, matched against the delivery (country wins, region falls back). */}
        <Field label="Territory">
          <div className="space-y-2">
            <Toggle
              checked={scoped}
              onChange={setScoped}
              label={scoped ? 'Apply to a specific territory' : 'Flat — applies to all deliveries'}
            />
            {scoped ? (
              <div className="flex flex-wrap gap-1.5">
                {FUEL_TERRITORIES.map((tt) => (
                  <button
                    key={tt.code}
                    type="button"
                    onClick={() => setTerritoryCode(tt.code)}
                    className={cn(
                      'inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors',
                      territoryCode === tt.code
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400',
                    )}
                  >
                    {tt.label}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">
                This % applies to every delivery for this customer, regardless of country or region.
              </p>
            )}
          </div>
        </Field>

        {/* Effective dates */}
        <Field label="Effective dates">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-[11px] font-medium text-slate-500">
              From
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-0.5 block w-44 rounded-sm border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </label>
            <label className={cn('text-[11px] font-medium text-slate-500', noExpiry && 'opacity-40')}>
              To
              <input
                type="date"
                value={to}
                disabled={noExpiry}
                onChange={(e) => setTo(e.target.value)}
                className="mt-0.5 block w-44 rounded-sm border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-50"
              />
            </label>
            <Toggle checked={noExpiry} onChange={setNoExpiry} label="No expiry" />
          </div>
        </Field>

        {/* Versioning warning — a new rate runs alongside the current one. */}
        {prior && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            The current active rate is <span className="font-semibold">{prior.rate}</span>. Adding a
            new rate will run alongside it unless you expire the prior one.
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-1 border-t border-slate-200 pt-3">
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Save"
            onClick={handleSave}
            disabled={!canSave}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      {children}
    </div>
  );
}
